// react
import { useEffect, useState } from "react";
import { BlockerFunction, useBlocker } from "react-router-dom";

interface BaseProps {
	message?: string;
	initialState?: boolean;
	disabled?: boolean;
	disableReactWarning?: boolean;
	blockSearchParamChanges?: boolean;
}

const useUnsavedChanges = (props?: BaseProps) => {
	const {
		message = "Are you sure you want to leave?",
		initialState = false,
		disabled = false,
		disableReactWarning = false,
		blockSearchParamChanges = true,
	} = props || {};

	const [isUnsafe, setIsUnsafe] = useState(initialState);

	const setIsUnsafeTrue = () => {
		setIsUnsafe(true);
	};

	const setIsUnsafeFalse = () => {
		setIsUnsafe(false);
	};

	// The confirm runs inside the blocker function, synchronously during the router's
	// navigation dispatch. Confirming from an effect after a "blocked" render stopped
	// working on react-router 7: every router state commit is wrapped in
	// React.startTransition, so the blocked render lands after the triggering event
	// has finished and the prompt raced whatever was listening for it (QTM-335).
	const shouldBlock: BlockerFunction = ({ currentLocation, nextLocation }) => {
		// Search-param-only changes are decided here rather than after the fact: the router hands
		// the blocker function both locations at navigation time, so the comparison can't go stale
		// the way a mount-time pathname snapshot did when a param-only navigation kept the page
		// mounted.
		if (!blockSearchParamChanges && currentLocation.pathname === nextLocation.pathname) {
			return false;
		}
		// The native dialog steals window focus; on Cancel the browser restores it to whatever
		// triggered the navigation, and a focus-activated control (a Radix tab trigger, e.g.)
		// re-fires that navigation — re-prompting forever. Park focus on <body> first.
		(document.activeElement as HTMLElement | null)?.blur?.();
		return !window.confirm(message);
	};

	const blocker = useBlocker(isUnsafe && !disabled && !disableReactWarning ? shouldBlock : false);

	// A cancelled prompt leaves the router in a "blocked" state; reset immediately so the
	// next navigation attempt evaluates the blocker again.
	useEffect(() => {
		if (blocker.state === "blocked") {
			blocker.reset();
		}
	}, [blocker]);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (isUnsafe && !disabled) {
				event.preventDefault();
				event.returnValue = message;
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [disabled, message, isUnsafe]);

	return {
		setIsSafe: setIsUnsafeFalse,
		setIsUnsafe: setIsUnsafeTrue,
		isUnsafe,
	};
};

export default useUnsavedChanges;
